import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets
 */

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {{ x: number, y: number } | null} */
  #savedScrollPosition = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #boundVariantChanged = this.variantChanged.bind(this);

  #boundPointerDown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const interactiveElement = event.target.closest(
      'input[type="radio"], input[type="checkbox"], select, label'
    );

    if (!interactiveElement) return;

    /*
     * Save the scroll position BEFORE the browser focuses the
     * variant input. This is important on mobile because the
     * browser can scroll the page as part of focusing the input.
     */
    this.#savedScrollPosition = {
      x: window.scrollX,
      y: window.scrollY,
    };
  };

  connectedCallback() {
    super.connectedCallback();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === 'true'
      );

      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    /*
     * pointerdown happens BEFORE focus/change.
     * This lets us record the real scroll position before
     * mobile Safari/Chrome has a chance to move the page.
     */
    this.addEventListener('pointerdown', this.#boundPointerDown, {
      passive: true,
    });

    this.addEventListener('change', this.#boundVariantChanged);

    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#boundPointerDown);
    this.removeEventListener('change', this.#boundVariantChanged);

    this.#resizeObserver.disconnect();
    this.#abortController?.abort();
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    /*
     * Fallback in case the change event was triggered without
     * a pointer interaction.
     */
    if (!this.#savedScrollPosition) {
      this.#savedScrollPosition = {
        x: window.scrollX,
        y: window.scrollY,
      };
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    /*
     * Keep the browser from using the focused variant input
     * as a reason to move the viewport.
     */
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement) {
      try {
        activeElement.focus({
          preventScroll: true,
        });
      } catch {
        // Ignore browsers without preventScroll support.
      }
    }

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldset = this.#radios[fieldsetIndex];

    if (!fieldset?.length) return null;

    const measurements = fieldset.map((radio) => {
      const label = radio.closest('label');

      if (!label) return null;

      const rect = label.getBoundingClientRect();

      return {
        width: rect.width,
        height: rect.height,
      };
    });

    return measurements;
  }

  #applyFieldsetMeasurements(fieldsetIndex, measurements) {
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !measurements) return;

    radios.forEach((radio, index) => {
      const measurement = measurements[index];

      if (!measurement) return;

      const label = radio.closest('label');

      if (!label) return;

      label.style.setProperty(
        '--variant-width',
        `${measurement.width}px`
      );

      label.style.setProperty(
        '--variant-height',
        `${measurement.height}px`
      );
    });
  }

  updateFieldsetCss(fieldsetIndex) {
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(fieldsetIndex, measurements);
    }
  }

  updateSelectedOption(target) {
    if (target instanceof HTMLSelectElement) {
      const selectedIndex = target.selectedIndex;

      target.querySelectorAll('option').forEach((option, index) => {
        if (index === selectedIndex) {
          option.setAttribute('selected', '');
        } else {
          option.removeAttribute('selected');
        }
      });

      return;
    }

    if (!(target instanceof HTMLInputElement)) return;

    const fieldset = target.closest('fieldset');

    if (!fieldset) return;

    fieldset.querySelectorAll('input').forEach((input) => {
      if (input === target) {
        input.checked = true;
        input.setAttribute('checked', '');
        input.dataset.currentChecked = 'true';
      } else {
        input.checked = false;
        input.removeAttribute('checked');
        input.dataset.currentChecked = 'false';
      }
    });
  }

  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    const url = new URL(
      this.dataset.productUrl || window.location.href,
      window.location.origin
    );

    const optionValueId = selectedOption.dataset.optionValueId;

    if (optionValueId) {
      const selectedOptions = this.selectedOptionsValues;

      if (source && sourceSelectedOptionsValues.length) {
        selectedOptions.splice(
          sourceSelectedOptionsValues.length - 1,
          1,
          optionValueId
        );
      }

      url.searchParams.set(
        'option_values',
        selectedOptions.join(',')
      );
    }

    url.searchParams.set(
      'section_id',
      this.dataset.sectionId || ''
    );

    return url.toString();
  }

  fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();

    this.#abortController = new AbortController();

    fetch(requestUrl, {
      signal: this.#abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        html
          .querySelector('overflow-list[defer]')
          ?.removeAttribute('defer');

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`
        )?.textContent;

        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(textContent),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                }
              )
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        /*
         * Important:
         * We intentionally DO NOT call window.scrollTo().
         *
         * The browser should remain at its original position
         * naturally rather than being repeatedly forced back.
         */
        this.#savedScrollPosition = null;
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;

    const newVariantPickerSource =
      newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId =
        newVariantPickerSource.dataset.productId;

      const newProductUrl =
        newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,

      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        const key = node.dataset.key;

        return key;
      },
    });

    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */
      (this.refs.fieldsets || []);

    const measurements = fieldsets
      .map((_, index) =>
        this.#getFieldsetMeasurements(index)
      )
      .filter((measurement) => measurement !== null);

    for (const measurement of measurements) {
      if (!measurement) continue;

      /*
       * Reapply the measurements without touching scroll.
       */
      measurement.forEach((_, index) => {
        const fieldsetIndex = measurements.indexOf(measurement);

        this.#applyFieldsetMeasurements(
          fieldsetIndex,
          measurement
        );
      });
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error('No option value ID found');
      }

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}